import addDays from 'date-fns/addDays';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';

import { featureEnabler } from '../../../utils';
import { TypeOfApplicationLink, UIDAPresentationTypeEnum, tvpil } from '../../data/compat/tvpil';
import type { Linear } from '../../data/compat/types';
import { createTypeGuard, getAssetContentMarker, getAssetEpisodeIndication, isAssetPurchasable } from '../asset';

import {
  CollectionAsset,
  CollectionEpisodeAsset,
  CollectionChannelAsset,
  CollectionLinkAsset,
  CollectionMovieAsset,
  CollectionProgramAsset,
  CollectionSeriesAsset,
  CollectionVodAsset,
  CollectionLinearAsset,
} from './types';

/**
 * @see https://kaltura.atlassian.net/wiki/spaces/KUX/pages/3626729600/HBO+Max+in+application+screens+Spec#New-tasks.1
 */
export function shouldPlayOnExternalApp(asset: CollectionAsset) {
  return (
    (isCollectionMovieAsset(asset) || isCollectionEpisodeAsset(asset)) &&
    Boolean(asset.deeplink) &&
    Boolean(asset.deeplinkUrn) &&
    !asset.hasMediaFiles
  );
}

export const isCollectionEpisodeAsset = createTypeGuard<CollectionAsset, CollectionEpisodeAsset>('episode');
export const isCollectionChannelAsset = createTypeGuard<CollectionAsset, CollectionChannelAsset>('linear');
export const isCollectionLinkAsset = createTypeGuard<CollectionAsset, CollectionLinkAsset>('link');
export const isCollectionMovieAsset = createTypeGuard<CollectionAsset, CollectionMovieAsset>('movie');
export const isCollectionProgramAsset = createTypeGuard<CollectionAsset, CollectionProgramAsset>('program');
export const isCollectionSeriesAsset = createTypeGuard<CollectionAsset, CollectionSeriesAsset>('series');

export const isCollectionVodAsset = createTypeGuard<CollectionAsset, CollectionVodAsset>('movie', 'episode', 'series');
export const isCollectionLinearAsset = createTypeGuard<CollectionAsset, CollectionLinearAsset>('linear', 'program');

export function createCollectionAssetLink(asset: CollectionLinkAsset): string | undefined {
  if (!asset.linkType || !asset.inAppLink) {
    return undefined;
  }

  return tvpil.generateMenuLink(
    asset.linkType as TypeOfApplicationLink,
    asset.inAppLink,
    asset.attachedCollectionId,
    featureEnabler.variation('IS_NEW_COLLECTION_GRID', false) ? UIDAPresentationTypeEnum.collection : undefined,
  );
}

export function isCollectionLinkAssetValid(asset: CollectionLinkAsset): boolean {
  if (asset.linkType === 'grid') {
    return (
      Boolean(asset.attachedCollectionId) && Boolean(asset.inAppLink && tvpil.categoryTreeItemsMap.has(asset.inAppLink))
    );
  }

  return true;
}

export function isCollectionInAppLinkAdult(asset: CollectionAsset) {
  return isCollectionLinkAsset(asset) && isInAppLinkAdult(asset.inAppLink);
}

function isInAppLinkAdult(inAppLink?: string) {
  return Boolean(inAppLink) && inAppLink === tvpil.dms.adultsConfigurations?.menuCategoryReferenceId;
}

export function isCollectionAssetPlayable(asset: CollectionAsset, channels: Map<number, Linear>) {
  const isPurchased = !isCollectionAssetPurchasable(asset, channels);

  const isPlayableCatchUp =
    isCollectionProgramAsset(asset) && asset.enableCatchUp && !asset.seriesId && asset.startDate <= Date.now();
  const isPlayableSingleVod = (isCollectionEpisodeAsset(asset) || isCollectionMovieAsset(asset)) && asset.hasMediaFiles;

  return (isPlayableSingleVod || isPlayableCatchUp || isCollectionChannelAsset(asset)) && isPurchased;
}

export function getCollectionAssetContentMarker(asset: CollectionAsset) {
  if (!isCollectionVodAsset(asset)) {
    return undefined;
  }

  return getAssetContentMarker(asset);
}

export function getProgramAiringDate(asset: CollectionProgramAsset) {
  const now = Date.now();
  const tomorrow = addDays(now, 1);

  const startOfTomorrow = startOfDay(tomorrow).getTime();
  const endOfTomorrow = endOfDay(addDays(now, 1)).getTime();

  const { startDate } = asset;

  const startHour = new Date(startDate).getHours();

  if (startDate < now) {
    return { startDate };
  }

  if (startDate < startOfTomorrow && tvpil.primeTime && startHour >= tvpil.primeTime) {
    return { startDate, label: 'tonight' };
  }

  if (startDate < startOfTomorrow) {
    return { startDate, label: 'today' };
  }

  if (startDate < endOfTomorrow) {
    return { startDate, label: 'tomorrow' };
  }

  return { startDate };
}

export function getCollectionAssetChannelId(asset: CollectionLinearAsset) {
  return isCollectionProgramAsset(asset) ? asset.channelId : asset.id;
}

export function isCollectionAssetPurchasable(asset: CollectionAsset, channels: Map<number, Linear>) {
  if (isCollectionLinearAsset(asset)) {
    return !channels.get(getCollectionAssetChannelId(asset))?.linearEntitlement;
  }

  if (isCollectionMovieAsset(asset) || isCollectionEpisodeAsset(asset)) {
    return isAssetPurchasable(asset);
  }

  return false;
}

export function getCollectionAssetTitle(asset: CollectionAsset) {
  const hasSeriesId = (isCollectionEpisodeAsset(asset) || isCollectionProgramAsset(asset)) && Boolean(asset.seriesId);

  if (hasSeriesId) {
    return asset.seriesName ?? asset.title;
  }

  return asset.title;
}

export function getCollectionAssetEpisodeIndication(asset: CollectionAsset) {
  if (!isCollectionEpisodeAsset(asset) && !isCollectionProgramAsset(asset)) {
    return undefined;
  }

  return getAssetEpisodeIndication(asset);
}

export function isCollectionEboxAsset(asset: CollectionAsset) {
  return isCollectionLinkAsset(asset) && asset.externalProvider === 'ebox';
}
